/**
 * CopyButton — inline "Copy [X]" affordance with built-in toast feedback.
 *
 * Per Fix Right pattern #6: any time the UI surfaces an ID, URL, slug, or
 * other short string the user is likely to paste somewhere else, wrap it in
 * a CopyButton. Saves the user a triple-click + cmd-C dance, and the toast
 * confirmation gives unambiguous "yes it copied" feedback.
 *
 * Security boundary (DO NOT BYPASS): never use this for tokens, OAuth
 * secrets, API keys, session JWTs, or any value classified as a credential.
 * Per UX-DESIGN.md "Never copy-tokens" — credentials must be re-fetched per
 * session, never sit in a clipboard buffer where another app can read them.
 *
 * Canonical example:
 *   <CopyButton value={instance.id} successMessage="Instance ID copied.">
 *     Copy ID
 *   </CopyButton>
 *
 * Falls back to a graceful error toast if `navigator.clipboard.writeText`
 * is unavailable or denied (e.g., HTTP origins, locked-down browsers).
 */

import type { ReactNode } from 'react';

import { useToast } from './Toast.tsx';
// Push 5-pre-smoke Part A3 (2026-07-11) — copy glyph consolidated onto
// the canonical IconCopy primitive so the CopyButton chip matches the
// message-hover Copy affordance + code-block Copy affordance.
import { IconCopy } from './Icon.tsx';

interface CopyButtonProps {
  /** The string actually copied to clipboard */
  value: string;
  /** Button label rendered next to the copy icon */
  children: ReactNode;
  /** Toast message on successful copy. Default "Copied to clipboard." */
  successMessage?: string;
}

export function CopyButton({ value, children, successMessage }: CopyButtonProps) {
  const { showToast } = useToast();

  function handleClick() {
    navigator.clipboard.writeText(value).then(
      () => {
        showToast({
          kind: 'success',
          message: successMessage ?? 'Copied to clipboard.',
        });
      },
      () => {
        showToast({
          kind: 'error',
          message: "Couldn't copy. Highlight the text and copy manually.",
        });
      },
    );
  }

  return (
    <button type="button" className="copy-btn" onClick={handleClick}>
      <IconCopy size={14} />
      {children}
    </button>
  );
}
